/* DT — Horizon Premium Reveal Engine (Soft Random + Re-run) v1.2.1
   PATCH:
   ✅ Forces above-the-fold elements to be visible on first paint (no blank until scroll)
*/
(() => {
  const ready = (fn) => {
    if (document.readyState === 'complete' || document.readyState === 'interactive') return fn();
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  ready(() => {
    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;

    // Auto targets (safe + premium)
    const SELECTORS = [
      '.shopify-section',
      '#MainContent h1, #MainContent h2, #MainContent h3, #MainContent h4',
      '#MainContent p, #MainContent li, #MainContent blockquote',
      '#MainContent .card, #MainContent .grid__item, #MainContent .product-card, #MainContent .collection-card',
      '#MainContent picture, #MainContent img, #MainContent video',
      '#MainContent .button, #MainContent button, #MainContent .btn',
      '#MainContent .rte > *'
    ];

    // Exclusions (avoid breaking UI)
    const EXCLUDE = [
      'header', '.header', '.header-wrapper',
      '.drawer', '.modal', '.cart-drawer', '.search-modal',
      '.announcement-bar',
      'input, textarea, select, option, label'
    ];

    const EFFECTS = [
      'dt-reveal--fade',
      'dt-reveal--up',
      'dt-reveal--left',
      'dt-reveal--right',
      'dt-reveal--zoom',
      'dt-reveal--tiltL',
      'dt-reveal--tiltR'
    ];

    const matchesAny = (el, list) => {
      try { return list.some(sel => el.matches(sel)); } catch (e) { return false; }
    };

    const isValid = (el) => {
      if (!el || el.nodeType !== 1) return false;

      // allow opt-out on any parent
      if (el.closest('[data-dt-reveal-off="1"]')) return false;

      // exclusions
      if (matchesAny(el, EXCLUDE)) return false;
      if (el.closest(EXCLUDE.join(','))) return false;

      // skip hidden/collapsed trees
      if (el.closest('[hidden], details:not([open]), [aria-hidden="true"]')) return false;

      const cs = getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;

      // skip very tiny nodes (icons, small spans)
      const r = el.getBoundingClientRect();
      if ((r.width * r.height) < 650) return false;

      return true;
    };

    // Stable random using FNV-ish hash
    const hashStr = (str) => {
      let h = 2166136261;
      for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return (h >>> 0);
    };

    const pickEffect = (el) => {
      // manual override: data-dt-reveal="left|right|up|zoom|fade|tiltL|tiltR"
      const forced = (el.getAttribute('data-dt-reveal') || '').trim().toLowerCase();
      if (forced) {
        const map = {
          fade: 'dt-reveal--fade',
          up: 'dt-reveal--up',
          left: 'dt-reveal--left',
          right: 'dt-reveal--right',
          zoom: 'dt-reveal--zoom',
          zoom2: 'dt-reveal--zoom2',
          tiltl: 'dt-reveal--tiltL',
          tiltr: 'dt-reveal--tiltR'
        };
        return map[forced] || null;
      }

      // stable seed (keeps same effect per element)
      const key = [
        el.tagName,
        el.className || '',
        el.id || '',
        el.getAttribute('href') || '',
        el.textContent ? el.textContent.slice(0, 40) : ''
      ].join('|');

      const h = hashStr(key);
      return EFFECTS[h % EFFECTS.length];
    };

    const mark = (el) => {
      if (!isValid(el)) return;
      if (el.dataset.dtRevealInit === '1') return;

      el.dataset.dtRevealInit = '1';
      el.classList.add('dt-reveal');

      // add one random effect class (if not already set)
      if (![...el.classList].some(c => c.startsWith('dt-reveal--'))) {
        const fx = pickEffect(el);
        if (fx) el.classList.add(fx);
      }
    };

    const prep = (root = document) => {
      // explicit user-marked
      root.querySelectorAll('[data-dt-reveal], .dt-reveal').forEach(mark);

      // auto-mark
      SELECTORS.forEach(sel => {
        root.querySelectorAll(sel).forEach(mark);
      });
    };

    // Cooldown to prevent flicker when hovering around threshold
    const COOLDOWN_MS = 180;

    const io = new IntersectionObserver((entries) => {
      const now = Date.now();

      entries.forEach(e => {
        const el = e.target;

        // per-element cooldown timestamp
        const last = Number(el.dataset.dtRevealT || 0);
        if (now - last < COOLDOWN_MS) return;

        if (e.isIntersecting) {
          el.classList.add('is-in');
          el.dataset.dtRevealT = String(now);
        } else {
          // leaving viewport => remove so it can replay
          el.classList.remove('is-in');
          el.dataset.dtRevealT = String(now);
        }
      });
    }, {
      threshold: 0.14,
      rootMargin: '0px 0px -12% 0px'
    });

    const observeAll = (root = document) => {
      root.querySelectorAll('.dt-reveal').forEach(el => {
        if (!isValid(el)) return;
        // always observe (we want replay)
        io.observe(el);
      });
    };

    const run = (root = document) => {
      prep(root);
      observeAll(root);
    };

    /* ✅ PATCH: immediately reveal anything already in/near viewport */
    const forceInViewNow = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;

      document.querySelectorAll('.dt-reveal').forEach(el => {
        if (!isValid(el)) return;

        const r = el.getBoundingClientRect();

        // if already visible (or near visible), show it instantly
        if (r.top < (vh - 40) && r.bottom > 0) {
          el.classList.add('is-in');
          // prevent cooldown from blocking initial reveal
          el.dataset.dtRevealT = String(Date.now() - (COOLDOWN_MS + 5));
        }
      });
    };

    // init
    run(document);
    forceInViewNow();
    window.addEventListener('load', () => setTimeout(forceInViewNow, 80), { once:true });

    // Shopify dynamic sections
    const onShopify = (e) => {
      run(e?.target || document);
      forceInViewNow();
    };
    document.addEventListener('shopify:section:load', onShopify);
    document.addEventListener('shopify:section:reorder', onShopify);
    document.addEventListener('shopify:section:select', onShopify);

    // Page restore / transitions
    window.addEventListener('pageshow', () => {
      setTimeout(() => { run(document); forceInViewNow(); }, 80);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        setTimeout(() => { run(document); forceInViewNow(); }, 80);
      }
    });

    // AJAX content changes
    let to = null;
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.addedNodes && m.addedNodes.length) {
          clearTimeout(to);
          to = setTimeout(() => { run(document); forceInViewNow(); }, 160);
          break;
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  });
})();